'use client';
import clsx from 'clsx';
import { Fragment, useCallback, useState } from 'react';
import { useSegmentInteraction } from '../../../../lib/analytics/useSegmentInteraction';
import { type L1MenuItem } from '../../../../services/globalNavItems/types';
import { useDropdownContext } from '../../../DropdownProvider';
import { TOGGLE_DESKTOP_NAV_ACTION } from '../../../DropdownProvider/useSingleDropdown';
import { Typography } from '../../../Typography';
import { L2Navigation } from '../L2Navigation';

export type L1NavigationProps = {
  l1Menu: L1MenuItem[];
};

export function L1Navigation({ l1Menu }: L1NavigationProps) {
  const { state, dispatch } = useDropdownContext();
  const [selectedL1NavItem, setSelectedL1NavItem] = useState<string | null>(null);
  const sendSegmentInteraction = useSegmentInteraction({
    eventName: 'global_nav_menu_interacted',
    attributes: {
      entity_name: 'global_nav_l1',
      item_level: 1,
      navigation_format: 'header',
    },
  });

  const handleClose = useCallback(() => {
    dispatch(TOGGLE_DESKTOP_NAV_ACTION);
    setSelectedL1NavItem(null);
  }, [dispatch]);

  const handleOpen = useCallback(
    (id: string, title: string, index: number) => {
      if (!state.isShowingDesktopNav) {
        dispatch(TOGGLE_DESKTOP_NAV_ACTION);
      }
      setSelectedL1NavItem(id);
      sendSegmentInteraction({ item_copy: title, item_position: index });
    },
    [dispatch, state.isShowingDesktopNav],
  );

  return (
    <ul className={clsx('flex', 'gap-3', 'p-0', 'm-0', 'xxl:gap-6')}>
      {l1Menu.map((item, index) => {
        const isSelected = selectedL1NavItem === item.id && state.isShowingDesktopNav;

        return (
          <Fragment key={item.id}>
            <li
              className={clsx(
                'list-none',
                'cursor-pointer',
                'pl-0',
                'pr-0',
                'pt-[calc(.25rem+2px)]',
                'pb-1',
                'border-b-2',
                'transition-border',
                'duration-200',
                {
                  'border-green-default': isSelected,
                  'border-transparent': !isSelected,
                  'hover:border-neutral-light': !isSelected,
                },
              )}
            >
              <button
                onClick={() => handleOpen(item.id, item.title, index)}
                aria-expanded={isSelected}
                aria-controls={`desktop-nav-level-1-menu-${item.title}`}
              >
                <Typography
                  variant="text-xsmall"
                  className={clsx(
                    '!font-medium',
                    'text-nowrap',
                    'xxl:text-medium',
                    'text-neutral-darkest',
                  )}
                >
                  {item.title}
                </Typography>
              </button>
            </li>
            {item.l2Menu && (
              <L2Navigation
                promoSpot={item.promoSpot}
                open={isSelected}
                onClose={handleClose}
                l2Menu={item.l2Menu}
                l1Title={item.title}
                aria-hidden={!isSelected}
                id={`desktop-nav-level-1-menu-${item.title}`}
                moreInfoLink={
                  item.moreInfoText && item.moreInfoLink?.uri
                    ? {
                        linkText: item.moreInfoText,
                        uri: item.moreInfoLink.uri,
                      }
                    : undefined
                }
              />
            )}
          </Fragment>
        );
      })}
    </ul>
  );
}
